import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {isLLMFeedbackEnabled} from 'llm/feedback/LLMFeedbackUtils';
import {
  CheckEntryDisplayResult,
  CheckEntryResultUsefulState,
} from 'notebook/ai-tools/NotebookAIToolsConstants';
import Checkbox from 'ui-library/Checkbox';
import {GRAY, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';

interface AICheckerResultProps {
  result: CheckEntryDisplayResult;
  onToggleCompleted: () => void;
  onToggleUseful: () => void;
  onToggleNotUseful: () => void;
}

/**
 * Individual result from the AI checker, which can be marked by the user as done or as not useful.
 */
export default function AICheckerResult({
  result,
  onToggleCompleted,
  onToggleUseful,
  onToggleNotUseful,
}: AICheckerResultProps): JSX.Element {
  const isUseful = result.usefulState === CheckEntryResultUsefulState.USEFUL;
  const isNotUseful = result.usefulState === CheckEntryResultUsefulState.NOT_USEFUL;
  const isMarked = result.usefulState !== CheckEntryResultUsefulState.UNMARKED;
  return (
    <div className={css(styles.checkResult)} data-test-component="AICheckerResult" data-test-element="root">
      <div className={css(styles.checkbox, isNotUseful && styles.checkResultNotUseful)}>
        <Checkbox checked={result.isCompleted} onChange={onToggleCompleted} />
      </div>
      <div>
        <span
          className={css(
            isNotUseful && styles.checkResultNotUseful,
            result.isCompleted && styles.checkResultCompleted
          )}
        >
          {result.text}
        </span>
        {isLLMFeedbackEnabled() && (
          <>
            <a
              className={classNames(
                'fa fa-thumbs-up',
                css(
                  styles.thumbsLink,
                  isNotUseful && styles.thumbsLinkInactive,
                  isMarked && styles.thumbsLinkVisible
                )
              )}
              onClick={onToggleUseful}
            />
            <a
              className={classNames(
                'fa fa-thumbs-down',
                css(
                  styles.thumbsLink,
                  isUseful && styles.thumbsLinkInactive,
                  isMarked && styles.thumbsLinkVisible
                )
              )}
              onClick={onToggleNotUseful}
            />
            {isMarked && (
              <span className={css(styles.usefulLabel)}>{isUseful ? 'Useful' : 'Not useful'}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  checkResult: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 5,
    paddingBottom: 5,
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
    ':hover .fa-thumbs-up': {
      visibility: 'visible',
    },
    ':hover .fa-thumbs-down': {
      visibility: 'visible',
    },
    lineHeight: 1.3,
  },
  checkbox: {
    display: 'flex',
    marginLeft: 10,
    marginRight: 10,
  },
  checkResultCompleted: {
    textDecoration: 'line-through',
  },
  checkResultNotUseful: {
    opacity: 0.4,
  },
  thumbsLink: {
    marginLeft: 10,
    visibility: 'hidden',
    fontSize: 14,
    display: 'inline',
    ':hover': {
      textDecoration: 'none',
    },
  },
  thumbsLinkInactive: {
    color: GRAY,
  },
  thumbsLinkVisible: {
    visibility: 'visible',
  },
  usefulLabel: {
    marginLeft: 10,
    backgroundColor: GRAY_LIGHT,
    borderRadius: 10,
    padding: '2px 5px',
    fontSize: 11,
    whiteSpace: 'nowrap',
  },
});
